import { useCallback, useMemo, useRef } from 'react';

import { useConnectedRouter } from '../../../../hooks/useConnectedRouter';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useShortUrl } from '../../../../hooks/useShortUrl';
import type { Topic, User, Vote } from '../../../../types/api';
import { AnswerBottomAction } from '../../../BottomActions/AnswerBottomAction';
import { ShareBottomAction } from '../../../BottomActions/ShareBottomAction';
import { VoteBottomAction } from '../../../BottomActions/VoteBottomAction';
import type { VoterInstance } from '../../../BottomActions/VoteBottomAction';
import type { VoteResp } from '../../../Vote/vote-model';

export const CardViewTopicBottomActions = ({
  topic,
  onVoteUpdate,
}: {
  topic: Topic;
  onVoteUpdate?: (updatedVotes: Vote[]) => void;
}) => {
  const { authUserId, isAuthDone } = usePermissions();
  const router = useConnectedRouter();

  const voteRef = useRef<VoterInstance>(null); // probably useless, since answering redirects.

  const shortUrl = useShortUrl({ pageId: topic.id as string, slug: topic.slug });

  const currentVote = useMemo(
    () => topic.votes?.find(elt => elt?.user?.id === authUserId),
    [authUserId, topic.votes],
  );

  const onVote = useCallback(
    async (data: VoteResp) => {
      const updatedVotes: Vote[] = data.voters.map(v => ({
        score: v.score,
        user: { __typename: 'User', id: v.user },
      }));
      onVoteUpdate?.(updatedVotes);
    },
    [onVoteUpdate],
  );

  const onAnswerClick = () => {
    router.push({ pathname: `/t/${topic.slug}`, query: { cardAnswer: true } });
  };

  return (
    <div className="TopicBottomActions flex justify-around">
      <AnswerBottomAction className="py-0 pr-0" answer={onAnswerClick} />
      <ShareBottomAction
        subject={topic.subject}
        content={topic.content as string}
        shortUrl={shortUrl}
      />
      {isAuthDone && authUserId !== (topic.author as User)?.id && (
        <VoteBottomAction
          currentVote={currentVote}
          url={`/api/page/${topic.id}/vote`}
          onVote={onVote}
          ref={voteRef}
        />
      )}
    </div>
  );
};
